import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Input, Tooltip, Button, Empty } from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  AppstoreOutlined, UnorderedListOutlined, CheckOutlined, ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";
import usePermissions from "../hooks/usePermissions";
import { authHeaders } from "../utils/session";
import ManageSmartSearchPlans from "../smart-search/ManageSmartSearchPlans";

/**
 * The subscription plan catalogue.
 *
 * Shown as pricing cards by default rather than the old two-panel browser: a
 * plan is pricing-page copy, and a card is the shape a customer will see it in,
 * so the person editing it is looking at the thing they are changing. The dense
 * table is there for the other job — comparing every plan's price and limits at
 * a glance.
 *
 * Deliberately the same screen language as Smart Search plans: two catalogues
 * that do the same job should not each invent their own layout.
 */

const BRAND = "#5b48e8";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
];

/** The two audiences a plan is written for. Colour follows the segment. */
const SEGMENTS = [
  { key: "all", label: "All segments" },
  { key: "attorney", label: "Attorney / IP" },
  { key: "corporate", label: "Corporate" },
];

const SEGMENT_TONE = {
  attorney: { color: "#4f46e5", bg: "#eef2ff", border: "#c7d2fe" },
  corporate: { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
};

const isAttorney = (p) => String(p?.is_for_attorney) === "1";
const segmentOf = (p) => (isAttorney(p) ? "attorney" : "corporate");
const segmentLabel = (p) => (isAttorney(p) ? "Attorney / IP" : "Corporate");
const isActive = (p) => String(p?.is_active) === "1";

const num = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : n.toLocaleString("en-IN");
};

/** Rupees exactly as the API reports them — nothing is rounded here. */
const rupees = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: Math.abs(n % 1) > 0 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

const featuresOf = (p, type) =>
  (Array.isArray(p?.features) ? p.features : []).filter((f) => f.feature_type === type);

const chip = (text, tone, extra = {}) => (
  <span style={{
    display: "inline-flex", alignItems: "center", padding: "2px 9px", borderRadius: 20,
    fontSize: 10.5, fontWeight: 700, letterSpacing: "0.3px", textTransform: "uppercase",
    color: tone.color, background: tone.bg, border: `1px solid ${tone.border}`,
    whiteSpace: "nowrap", ...extra,
  }}>
    {text}
  </span>
);

const CARD = {
  background: "#fff", borderRadius: 18, border: "1px solid #eceef5",
  boxShadow: "0 1px 3px rgba(15,23,42,0.04), 0 12px 30px rgba(15,23,42,0.05)",
  padding: 22, display: "flex", flexDirection: "column", position: "relative",
  transition: "box-shadow .2s ease, transform .2s ease",
  // Belt and braces: nothing inside may widen the grid column.
  minWidth: 0, overflowWrap: "anywhere",
};

const CardSkeleton = () => (
  <div style={{ ...CARD, minHeight: 380 }}>
    {[["45%", 16], ["68%", 30], ["38%", 12]].map(([w, h], i) => (
      <div key={i} style={{
        width: w, height: h, borderRadius: 7, background: "#f1f4f9",
        marginBottom: 12, animation: "mspPulse 1.3s ease-in-out infinite",
      }} />
    ))}
    <div style={{ height: 1, background: "#f1f4f9", margin: "10px 0 16px" }} />
    {[1, 2, 3, 4].map((i) => (
      <div key={i} style={{
        width: `${88 - i * 7}%`, height: 11, borderRadius: 6, background: "#f4f6fa",
        marginBottom: 10, animation: "mspPulse 1.3s ease-in-out infinite",
      }} />
    ))}
  </div>
);

/** One plan, as the customer would see it. */
const PlanCard = ({ plan, canEdit, canDelete, onToggle, onDelete }) => {
  const live = isActive(plan);
  const tone = SEGMENT_TONE[segmentOf(plan)];
  const limits = featuresOf(plan, "numeric");
  const included = featuresOf(plan, "boolean");
  const annual = rupees(plan.annual_price);

  return (
    <div
      style={{
        ...CARD,
        /* Dimmed, not hidden: an inactive plan is still a thing being managed,
           and it has to stay readable to be turned back on. */
        opacity: live ? 1 : 0.62,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(15,23,42,0.05), 0 18px 42px rgba(91,72,232,0.13)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = CARD.boxShadow;
        e.currentTarget.style.transform = "";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", overflowWrap: "anywhere" }}>
          {plan.name}
        </span>
        {chip(segmentLabel(plan), tone)}
        {!live && chip("Inactive", { color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" })}
      </div>

      {plan.description && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 14, lineHeight: 1.45 }}>
          {plan.description}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
        <span style={{ fontSize: 30, fontWeight: 800, color: "#0f172a", letterSpacing: -0.8, fontVariantNumeric: "tabular-nums" }}>
          {rupees(plan.monthly_price) ?? "—"}
        </span>
        {/* The figure alone reads as the whole plan price; it is charged per
            trademark, on both cycles. */}
        <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>/mo per trademark</span>
      </div>
      {annual && (
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
          or <b style={{ color: "#475569" }}>{annual}</b> /yr per trademark
        </div>
      )}

      <div style={{ height: 1, background: "#f1f4f9", margin: "16px 0" }} />

      {limits.length > 0 && (
        <div style={{ marginBottom: included.length ? 14 : 0 }}>
          <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
            Plan limits
          </div>
          {limits.map((f) => (
            <div key={f.id} style={{
              display: "flex", alignItems: "baseline", gap: 10, padding: "6px 0",
              borderBottom: "1px solid #f8fafc",
            }}>
              {/* These names run long and are written with slashes rather than
                  spaces, so there is nothing for the browser to break on — it
                  is allowed to break anywhere instead. `minWidth: 0` is what
                  lets a flex child shrink below its content at all; without it
                  the text pushes straight out of the card. */}
              <span style={{
                fontSize: 12, color: "#475569", lineHeight: 1.4,
                flex: 1, minWidth: 0, overflowWrap: "anywhere",
              }}>
                {f.name}
              </span>
              <span style={{
                flexShrink: 0, fontSize: 12.5, fontWeight: 700, color: "#0f172a",
                fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
              }}>
                {num(f.default_value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {included.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Included
          </div>
          {included.map((f) => (
            <div key={f.id} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{
                width: 16, height: 16, borderRadius: 99, background: "#eef2ff",
                color: BRAND, display: "inline-flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0, marginTop: 1,
              }}>
                <CheckOutlined style={{ fontSize: 9 }} />
              </span>
              <span style={{
                fontSize: 12.5, color: "#475569", lineHeight: 1.45,
                minWidth: 0, overflowWrap: "anywhere",
              }}>
                {f.name}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Pushed to the bottom so every card's actions sit on one line, however
          many features it carries. */}
      <div style={{
        marginTop: "auto", paddingTop: 14, borderTop: "1px solid #f1f4f9",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <Tooltip title={live ? "Deactivate this plan" : "Activate this plan"}>
          <span
            role="button"
            tabIndex={0}
            onClick={() => onToggle(plan)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(plan); } }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
              padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              color: live ? "#059669" : "#64748b",
              background: live ? "#ecfdf5" : "#f1f5f9",
              border: `1px solid ${live ? "#a7f3d0" : "#e2e8f0"}`,
            }}
          >
            <span style={{
              width: 6, height: 6, borderRadius: 99,
              background: live ? "#10b981" : "#94a3b8",
            }} />
            {live ? "Active" : "Inactive"}
          </span>
        </Tooltip>

        <div style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
          {canEdit && (
            <Tooltip title="Edit">
              <Link to={`/cms/manage-plan-new/edit-subscription-plan-new/${plan.id}`}>
                <Button size="small" icon={<EditOutlined />} />
              </Link>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(plan)} />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

/** The same plans, dense — for comparing rather than reading. */
const PlanTable = ({ plans, canEdit, canDelete, onToggle, onDelete }) => {
  const th = {
    textAlign: "left", padding: "11px 14px", fontSize: 10.5, fontWeight: 700,
    color: "#8b95a8", textTransform: "uppercase", letterSpacing: "0.5px",
    background: "#fafbff", borderBottom: "1px solid #eef0f6", whiteSpace: "nowrap",
  };
  const td = { padding: "12px 14px", borderBottom: "1px solid #f6f8fc", fontSize: 12.5 };

  return (
    <div style={{
      background: "#fff", borderRadius: 16, border: "1px solid #eceef5",
      overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
    }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 940 }}>
          <thead>
            <tr>
              {["Plan", "Segment", "Monthly", "Annual", "Limits", "Included", "Status", ""]
                .map((h, i) => (
                  <th key={i} style={{ ...th, textAlign: i >= 2 && i <= 5 ? "right" : "left" }}>{h}</th>
                ))}
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => {
              const live = isActive(p);
              const annual = rupees(p.annual_price);
              return (
                <tr key={p.id} style={{ opacity: live ? 1 : 0.6 }}>
                  <td style={{ ...td, fontWeight: 700, color: "#0f172a" }}>
                    {p.name}
                    {p.description && (
                      <div style={{
                        fontSize: 11, color: "#94a3b8", fontWeight: 400, marginTop: 2,
                        maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {p.description}
                      </div>
                    )}
                  </td>
                  <td style={td}>{chip(segmentLabel(p), SEGMENT_TONE[segmentOf(p)])}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {rupees(p.monthly_price) ?? "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right", color: annual ? "#475569" : "#cbd5e1", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {annual ?? "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right", color: "#475569" }}>
                    {featuresOf(p, "numeric").length || "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right", color: "#475569" }}>
                    {featuresOf(p, "boolean").length || "—"}
                  </td>
                  <td style={td}>
                    <Tooltip title={live ? "Deactivate this plan" : "Activate this plan"}>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => onToggle(p)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(p); } }}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
                          padding: "2px 9px", borderRadius: 20, fontSize: 10.5, fontWeight: 700,
                          color: live ? "#059669" : "#64748b",
                          background: live ? "#ecfdf5" : "#f1f5f9",
                          border: `1px solid ${live ? "#a7f3d0" : "#e2e8f0"}`,
                        }}
                      >
                        <span style={{ width: 6, height: 6, borderRadius: 99, background: live ? "#10b981" : "#94a3b8" }} />
                        {live ? "Active" : "Inactive"}
                      </span>
                    </Tooltip>
                  </td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    {canEdit && (
                      <Link to={`/cms/manage-plan-new/edit-subscription-plan-new/${p.id}`}>
                        <Button size="small" icon={<EditOutlined />} style={{ marginRight: 6 }} />
                      </Link>
                    )}
                    {canDelete && (
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(p)} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* Two catalogues, one screen. Smart Search plans were a separate page with a
   near-identical layout; a customer buying either is buying a subscription, so
   they belong in the same place under a tab rather than in two menu items that
   look the same. */
const CATALOGUES = [
  { key: "subscription", label: "Subscription Plans", icon: <AppstoreOutlined /> },
  { key: "smart_search", label: "Smart Search Plans", icon: <ThunderboltOutlined /> },
];

const TAB_KEY = "cms_plans_catalogue";
const readTab = () => {
  try {
    return localStorage.getItem(TAB_KEY) === "smart_search" ? "smart_search" : "subscription";
  } catch {
    return "subscription";
  }
};

const VIEW_KEY = "cms_sub_plans_view";
const readView = () => {
  try { return localStorage.getItem(VIEW_KEY) === "table" ? "table" : "grid"; } catch { return "grid"; }
};

const ManageSubPlanNew = () => {
  const { canCreate, canEdit, canDelete } = usePermissions("/cms/manage-subscription-plan");

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [segment, setSegment] = useState("all");
  const [view, setView] = useState(readView);
  const [catalogue, setCatalogue] = useState(readTab);

  const chooseCatalogue = (key) => {
    setCatalogue(key);
    try { localStorage.setItem(TAB_KEY, key); } catch { /* not worth failing over */ }
  };

  const chooseView = (v) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* not worth failing over */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE_URL}/get-all-plans-v1`, {
        headers: { ...authHeaders() },
      });
      setPlans(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      /* The API explains itself in the body on a failure; axios's own
         "Request failed with status code 500" tells the reader nothing. */
      const body = err?.response?.data;
      setError(body?.message || body?.msg || err.message || "Plans could not be loaded");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onToggle = (plan) => {
    const next = isActive(plan) ? 0 : 1;
    Swal.fire({
      title: next ? `Activate "${plan.name}"?` : `Deactivate "${plan.name}"?`,
      text: next
        ? "Customers will be able to subscribe to this plan."
        : "This hides the plan, so no new customer can subscribe to it. Existing subscriptions keep running.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: next ? "Activate" : "Deactivate",
      confirmButtonColor: next ? BRAND : "#d97706",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await axios.post(
          `${API_BASE_URL}/plans/${plan.id}/status`,
          { is_active: next },
          { headers: { ...authHeaders() } },
        );
        toast.success(res.data?.message || (next ? "Plan activated." : "Plan deactivated."));
        load();
      } catch (err) {
        const body = err?.response?.data;
        toast.error(body?.message || body?.msg || "The status could not be changed");
      }
    });
  };

  const onDelete = (plan) => {
    Swal.fire({
      title: `Delete "${plan.name}"?`,
      text: "This removes the plan from the catalogue. It cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await axios.post(
          `${API_BASE_URL}/delete-plans/${plan.id}`,
          {},
          { headers: { ...authHeaders() } },
        );
        toast.success(res.data?.message || "Plan deleted.");
        load();
      } catch (err) {
        const body = err?.response?.data;
        toast.error(body?.message || body?.msg || "The plan could not be deleted");
      }
    });
  };

  /* Filtered in the browser: this is a catalogue of a handful of plans, not a
     paged list, so a round trip per keystroke would be slower and no more
     correct. */
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return plans.filter((p) => {
      if (filter === "active" && !isActive(p)) return false;
      if (filter === "inactive" && isActive(p)) return false;
      if (segment !== "all" && segmentOf(p) !== segment) return false;
      if (!q) return true;
      return [p.name, p.description].some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [plans, search, filter, segment]);

  const counts = useMemo(() => ({
    all: plans.length,
    active: plans.filter(isActive).length,
    inactive: plans.filter((p) => !isActive(p)).length,
  }), [plans]);

  const css = `
    @keyframes mspPulse { 0%,100%{opacity:1} 50%{opacity:.5} }
    @keyframes mspFadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    .msp-card { animation: mspFadeUp .3s ease both; }
  `;

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <style>{css}</style>
          <div style={{
            background: "#f6f7fc", margin: "-24px -24px 0", padding: "26px 28px",
            minHeight: "100vh", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
          }}>

            {/* ── Header ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
              <div style={{
                width: 42, height: 42, borderRadius: 13,
                background: `linear-gradient(135deg, ${BRAND}, #7c3aed)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: 17, boxShadow: "0 6px 18px rgba(91,72,232,0.3)",
              }}>
                <AppstoreOutlined />
              </div>
              <div>
                <div style={{ fontSize: 19, fontWeight: 700, color: "#0f172a" }}>Manage Plans</div>
                <div style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 2 }}>
                  Everything a customer can subscribe to
                </div>
              </div>
              {catalogue === "subscription" && canCreate && (
                <Link to="/cms/manage-plan-new/add-subscription-plan-new" style={{ marginLeft: "auto" }}>
                  <Button
                    type="primary" icon={<PlusOutlined />}
                    style={{
                      background: BRAND, borderColor: BRAND, fontWeight: 600,
                      height: 38, borderRadius: 10,
                      boxShadow: "0 4px 14px rgba(91,72,232,0.28)",
                    }}
                  >
                    Add Plan
                  </Button>
                </Link>
              )}
            </div>

            {/* ── Catalogue tabs ── */}
            <div style={{
              display: "flex", gap: 18, borderBottom: "1px solid #eae8f2",
              marginBottom: 18, flexWrap: "wrap",
            }}>
              {CATALOGUES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => chooseCatalogue(c.key)}
                  aria-pressed={catalogue === c.key}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7,
                    background: "none", border: "none", cursor: "pointer",
                    padding: "9px 2px", fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                    color: catalogue === c.key ? BRAND : "#64748b",
                    borderBottom: `2px solid ${catalogue === c.key ? BRAND : "transparent"}`,
                  }}
                >
                  {c.icon} {c.label}
                </button>
              ))}
            </div>

            {catalogue === "smart_search" ? <ManageSmartSearchPlans embedded /> : (
              <>

            {/* ── Controls ── */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap",
              background: "#fff", border: "1px solid #eceef5", borderRadius: 14,
              padding: "12px 14px", boxShadow: "0 1px 3px rgba(15,23,42,0.03)",
            }}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: "#c0c6d4" }} />}
                placeholder="Search by name or description"
                style={{ width: 290, borderRadius: 9 }}
              />

              <div style={{ display: "inline-flex", gap: 6 }}>
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    style={{
                      padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                      cursor: "pointer", whiteSpace: "nowrap",
                      border: `1px solid ${filter === f.key ? "#c7d2fe" : "#e8eaf2"}`,
                      background: filter === f.key ? "#eef2ff" : "#fff",
                      color: filter === f.key ? BRAND : "#64748b",
                    }}
                  >
                    {f.label}
                    <span style={{ marginLeft: 6, opacity: 0.65, fontWeight: 500 }}>{counts[f.key]}</span>
                  </button>
                ))}
              </div>

              {/* The two audiences were the old screen's top-level split, so they
                  stay a first-class filter rather than something to search for. */}
              <div style={{ display: "inline-flex", gap: 6 }}>
                {SEGMENTS.map((sgm) => (
                  <button
                    key={sgm.key}
                    onClick={() => setSegment(sgm.key)}
                    style={{
                      padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                      cursor: "pointer", whiteSpace: "nowrap",
                      border: `1px solid ${segment === sgm.key ? "#c7d2fe" : "#e8eaf2"}`,
                      background: segment === sgm.key ? "#eef2ff" : "#fff",
                      color: segment === sgm.key ? BRAND : "#64748b",
                    }}
                  >
                    {sgm.label}
                  </button>
                ))}
              </div>

              <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Tooltip title="Reload">
                  <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }} />
                </Tooltip>
                <div style={{ display: "inline-flex", background: "#f4f5fa", borderRadius: 10, padding: 3 }}>
                  {[
                    { key: "grid", icon: <AppstoreOutlined />, title: "Pricing cards" },
                    { key: "table", icon: <UnorderedListOutlined />, title: "Dense list" },
                  ].map((v) => (
                    <Tooltip key={v.key} title={v.title}>
                      <button
                        onClick={() => chooseView(v.key)}
                        aria-pressed={view === v.key}
                        style={{
                          border: "none", cursor: "pointer", borderRadius: 8,
                          padding: "6px 13px", fontSize: 13,
                          background: view === v.key ? "#fff" : "transparent",
                          color: view === v.key ? BRAND : "#94a3b8",
                          boxShadow: view === v.key ? "0 1px 4px rgba(15,23,42,0.08)" : "none",
                        }}
                      >
                        {v.icon}
                      </button>
                    </Tooltip>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Body ── */}
            {loading ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 18 }}>
                {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
              </div>
            ) : error ? (
              <div style={{
                background: "#fff8f6", border: "1px dashed #f3c6b5", borderRadius: 14,
                padding: "40px 20px", textAlign: "center", color: "#9a3412", fontSize: 13.5,
              }}>
                {error}
                <div style={{ marginTop: 12 }}>
                  <Button onClick={load} icon={<ReloadOutlined />}>Try again</Button>
                </div>
              </div>
            ) : visible.length === 0 ? (
              <div style={{
                background: "#fff", border: "1px solid #eceef5", borderRadius: 16,
                padding: "52px 20px", textAlign: "center",
              }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={(
                    <span style={{ color: "#94a3b8", fontSize: 13 }}>
                      {plans.length === 0
                        ? "No subscription plans yet."
                        : "No plan matches these filters."}
                    </span>
                  )}
                >
                  {plans.length === 0 && canCreate && (
                    <Link to="/cms/manage-plan-new/add-subscription-plan-new">
                      <Button
                        type="primary" icon={<PlusOutlined />}
                        style={{ background: BRAND, borderColor: BRAND, fontWeight: 600 }}
                      >
                        Create the first plan
                      </Button>
                    </Link>
                  )}
                </Empty>
              </div>
            ) : view === "grid" ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 18, alignItems: "start" }}>
                {visible.map((p, i) => (
                  <div key={p.id} className="msp-card" style={{ animationDelay: `${Math.min(i, 8) * 0.04}s` }}>
                    <PlanCard
                      plan={p}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onToggle={onToggle}
                      onDelete={onDelete}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <PlanTable
                plans={visible}
                canEdit={canEdit}
                canDelete={canDelete}
                onToggle={onToggle}
                onDelete={onDelete}
              />
            )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageSubPlanNew;
